import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Search, Check, X, Trash2, Printer, 
  Calendar, User, DollarSign, Clock, ArrowRight, 
  AlertTriangle, RefreshCcw, Filter, ShoppingBag, ChevronDown, ChevronUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { confirmarPresupuesto } from '../utils/presupuestoConfirmacion.js';

let saleItemsCreatedAtColumnAvailable = null;
const SALE_ITEMS_CREATED_AT_SUPPORT_KEY = 'impulza_sale_items_created_at_support';

const getSaleItemsCreatedAtColumnSupport = () => {
  if (saleItemsCreatedAtColumnAvailable !== null) {
    return saleItemsCreatedAtColumnAvailable;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(SALE_ITEMS_CREATED_AT_SUPPORT_KEY);
    if (rawValue === 'true') {
      saleItemsCreatedAtColumnAvailable = true;
      return true;
    }
    if (rawValue === 'false') {
      saleItemsCreatedAtColumnAvailable = false;
      return false;
    }
  } catch {
    // Ignore storage access issues and continue with runtime detection.
  }

  return null;
};

const setSaleItemsCreatedAtColumnSupport = (value) => {
  saleItemsCreatedAtColumnAvailable = Boolean(value);

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(
      SALE_ITEMS_CREATED_AT_SUPPORT_KEY,
      value ? 'true' : 'false',
    );
  } catch {
    // Ignore storage access issues.
  }
};

const isMissingSaleItemsCreatedAtError = (error) => {
  const details = [error?.message, error?.details, error?.hint, error?.code]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return details.includes('created_at');
};

const attachSaleItemsToBudgets = async (sales = []) => {
  const normalizedSales = Array.isArray(sales) ? sales : [];
  const saleIds = Array.from(new Set(normalizedSales.map((sale) => sale.id).filter(Boolean)));

  if (saleIds.length === 0) {
    return normalizedSales.map((sale) => ({ ...sale, sale_items: [] }));
  }

  const buildSaleItemsQuery = (withCreatedAtOrder) => {
    let query = supabase
      .from('sale_items')
      .select('id, sale_id, product_id, variant_id, quantity, unit_price, product_name_snapshot')
      .in('sale_id', saleIds);

    if (withCreatedAtOrder) {
      query = query.order('created_at', { ascending: true });
    }

    return query;
  };

  let saleItemsResponse;
  const createdAtSupport = getSaleItemsCreatedAtColumnSupport();

  if (createdAtSupport !== false) {
    saleItemsResponse = await buildSaleItemsQuery(true);

    if (!saleItemsResponse.error) {
      setSaleItemsCreatedAtColumnSupport(true);
    } else if (isMissingSaleItemsCreatedAtError(saleItemsResponse.error)) {
      setSaleItemsCreatedAtColumnSupport(false);
      saleItemsResponse = await buildSaleItemsQuery(false);
    }
  } else {
    saleItemsResponse = await buildSaleItemsQuery(false);
  }

  if (saleItemsResponse.error) throw saleItemsResponse.error;
  const saleItems = saleItemsResponse.data || [];

  const productIds = Array.from(
    new Set((saleItems || []).map((item) => item.product_id).filter(Boolean)),
  );
  let productsById = new Map();

  if (productIds.length > 0) {
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('id, name, barcode')
      .in('id', productIds);

    if (productsError) throw productsError;

    productsById = new Map((products || []).map((product) => [product.id, product]));
  }

  const saleItemsBySaleId = (saleItems || []).reduce((acc, item) => {
    const product = item.product_id ? productsById.get(item.product_id) || null : null;
    const nextItem = {
      ...item,
      products: product
        ? { name: product.name || item.product_name_snapshot || 'Item', barcode: product.barcode || null }
        : { name: item.product_name_snapshot || 'Item', barcode: null },
    };

    if (!acc.has(item.sale_id)) {
      acc.set(item.sale_id, []);
    }

    acc.get(item.sale_id).push(nextItem);
    return acc;
  }, new Map());

  return normalizedSales.map((sale) => ({
    ...sale,
    sale_items: saleItemsBySaleId.get(sale.id) || [],
  }));
};

const Presupuestos = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [presupuestos, setPresupuestos] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  // Filtros: Fecha vacía por defecto para mostrar TODO lo vigente
  const [busqueda, setBusqueda] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    if (profile?.organization_id) {
      inicializarModulo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Ejecución secuencial: Primero limpia vencidos -> Luego carga los vigentes
  const inicializarModulo = async () => {
    setLoading(true);
    await limpiarVencidos();
    await fetchPresupuestos();
    setLoading(false);
  };

  // 1. Limpieza Automática (1 mes después de creación)
  const limpiarVencidos = async () => {
    try {
      const fechaLimite = new Date();
      fechaLimite.setDate(fechaLimite.getDate() - 30); // 30 días atrás
      
      const { error } = await supabase
        .from('sales')
        .update({ status: 'cancelled', active: false })
        .eq('organization_id', profile.organization_id)
        .eq('status', 'pending') // Solo presupuestos
        .lt('created_at', fechaLimite.toISOString());
        
      if (error) console.error("Error limpieza auto:", error);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPresupuestos = async () => {
    try {
      let query = supabase
        .from('sales')
        .select(`
          *,
          clients(full_name, doc_number),
          sitios(nombre)
        `)
        .eq('organization_id', profile.organization_id)
        .eq('status', 'pending') // FILTRO CLAVE: Solo Presupuestos ('pending')
        .order('created_at', { ascending: false });

      // Solo filtra por fecha si el usuario seleccionó una explícitamente
      if (filtroFecha) {
        query = query.gte('created_at', `${filtroFecha}T00:00:00`).lte('created_at', `${filtroFecha}T23:59:59`);
      }

      const { data, error } = await query;
      if (error) throw error;
      const budgetsWithItems = await attachSaleItemsToBudgets(data || []);
      setPresupuestos(budgetsWithItems);
    } catch (error) {
      toast.error('Error cargando presupuestos');
      console.error(error);
    }
  };

  // --- LÓGICA DE CONFIRMACIÓN (Transformar en Venta) ---
  // La lógica de negocio (stock + kardex, caja/banco vs. cuenta corriente,
  // idempotencia) vive en src/utils/presupuestoConfirmacion.js y replica
  // exactamente lo que hace una venta real del POS (VentasPOS.jsx).
  const handleConfirmar = async (presupuesto) => {
    if (!window.confirm("¿Confirmar presupuesto y transformar en VENTA REAL?\nSe descontará stock y se registrará el ingreso.")) return;

    setProcessingId(presupuesto.id);
    const toastId = toast.loading("Procesando venta...");

    try {
      const orgId = profile.organization_id;

      const [clienteRes, organizacionRes, metodoPagoRes] = await Promise.all([
        presupuesto.client_id
          ? supabase.from('clients').select('*').eq('id', presupuesto.client_id).maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        supabase.from('organizations').select('*').eq('id', orgId).maybeSingle(),
        presupuesto.payment_method_id
          ? supabase.from('payment_methods').select('*').eq('id', presupuesto.payment_method_id).maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (clienteRes.error) throw clienteRes.error;
      if (organizacionRes.error) throw organizacionRes.error;
      if (metodoPagoRes.error) throw metodoPagoRes.error;

      // Turno de caja abierto de la sucursal del presupuesto, igual que en el POS.
      let turnoCajaId = null;
      if (presupuesto.site_id) {
        const { data: turnoAbierto, error: turnoError } = await supabase
          .from('turnos_caja')
          .select('id')
          .eq('organization_id', orgId)
          .eq('estado', 'Abierto')
          .eq('sitio_id', presupuesto.site_id)
          .maybeSingle();

        if (turnoError) throw turnoError;
        turnoCajaId = turnoAbierto?.id || null;
      }

      const resultado = await confirmarPresupuesto({
        presupuesto,
        organizationId: orgId,
        userId: profile.id,
        cliente: clienteRes.data,
        organizacion: organizacionRes.data,
        metodoPago: metodoPagoRes.data,
        turnoCajaId,
      });

      if (!resultado.ok) {
        toast.error("Este presupuesto ya fue confirmado.", { id: toastId });
        fetchPresupuestos();
        return;
      }

      toast.success("¡Venta confirmada con éxito!", { id: toastId });
      fetchPresupuestos(); // Recarga la lista (el confirmado desaparecerá)

    } catch (e) {
      console.error(e);
      toast.error("Error al confirmar: " + e.message, { id: toastId });
    } finally {
      setProcessingId(null);
    }
  };

  // --- LÓGICA DE CANCELACIÓN ---
  const handleCancelar = async (id) => {
    if (!window.confirm("¿Cancelar este presupuesto definitivamente?")) return;
    setProcessingId(id);
    try {
        await supabase.from('sales').update({ status: 'cancelled', active: false }).eq('id', id);
        toast.success("Presupuesto cancelado");
        fetchPresupuestos();
    } catch (e) {
        toast.error("Error al cancelar");
    } finally {
        setProcessingId(null);
    }
  };

  // --- PDF (Simple) ---
  const imprimirPresupuesto = (venta) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(18);
    doc.text("PRESUPUESTO", W / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Fecha: ${new Date(venta.created_at).toLocaleDateString()}`, 10, 25);
    doc.text(`Cliente: ${venta.clients?.full_name || 'Consumidor Final'}`, 10, 30);
    doc.text(`ID Referencia: #${String(venta.id).slice(0,8).toUpperCase()}`, 10, 35);
    
    doc.text("Este documento no es válido como factura.", W - 10, 25, { align: 'right' });
    doc.text(`Válido por 30 días.`, W - 10, 30, { align: 'right' });

    const rows = (venta.sale_items || []).map(item => [
        item.products?.name || 'Item',
        item.quantity,
        `$ ${Number(item.unit_price).toLocaleString('es-AR')}`,
        `$ ${Number(item.quantity * item.unit_price).toLocaleString('es-AR')}`
    ]);

    autoTable(doc, {
        startY: 45,
        head: [['Producto', 'Cant', 'Unitario', 'Total']],
        body: rows,
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.text(`Total: $ ${Number(venta.total).toLocaleString('es-AR')}`, W - 10, finalY, { align: 'right' });

    window.open(doc.output('bloburl'), '_blank');
  };

  // Filtrado
  const dataFiltrada = useMemo(() => {
    const q = busqueda.toLowerCase();
    return presupuestos.filter(p => 
        (p.clients?.full_name || '').toLowerCase().includes(q) ||
        String(p.id).toLowerCase().includes(q)
    );
  }, [presupuestos, busqueda]);

  const getSortableValue = (item, key) => {
    switch (key) {
      case 'created_at':
        return new Date(item?.created_at || 0).getTime();
      case 'cliente':
        return String(item?.clients?.full_name || 'Consumidor Final').toLowerCase();
      case 'sucursal':
        return String(item?.sitios?.nombre || 'General').toLowerCase();
      case 'total':
        return Number(item?.total || 0);
      default:
        return '';
    }
  };

  const sortedDataFiltrada = useMemo(() => {
    const items = [...dataFiltrada];
    const { key, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;

    items.sort((a, b) => {
      const aValue = getSortableValue(a, key);
      const bValue = getSortableValue(b, key);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * factor;
      }

      return String(aValue).localeCompare(String(bValue), 'es-AR', { numeric: true, sensitivity: 'base' }) * factor;
    });

    return items;
  }, [dataFiltrada, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'created_at' || key === 'total' ? 'desc' : 'asc' }
    ));
  };

  const FilterHint = ({ text }) => (
    <span
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-200 bg-white text-[9px] font-black text-slate-400 cursor-help"
      title={text}
    >
      ?
    </span>
  );

  const SortableTh = ({ label, sortKey, align = 'left', title }) => {
    const isActive = sortConfig.key === sortKey;
    const Icon = isActive && sortConfig.direction === 'asc' ? ChevronUp : ChevronDown;
    const alignClass = align === 'center' ? 'justify-center text-center' : align === 'right' ? 'justify-end text-right' : 'justify-start text-left';

    return (
      <th className={`px-3.5 py-3 ${align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'}`}>
        <button
          type="button"
          onClick={() => handleSort(sortKey)}
          title={title || `Ordenar por ${label}`}
          className={`inline-flex w-full items-center gap-1.5 font-black uppercase tracking-widest transition-colors ${alignClass} ${isActive ? 'text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <span>{label}</span>
          <Icon size={12} className={isActive ? 'text-[#0B2A36]' : 'text-slate-300'} />
        </button>
      </th>
    );
  };

  const totalPendiente = sortedDataFiltrada.reduce((acc, curr) => acc + Number(curr.total), 0);

  // Helper para calcular fecha de vencimiento visual
  const getVencimiento = (fechaCreacion) => {
      const d = new Date(fechaCreacion);
      d.setDate(d.getDate() + 30);
      return d.toLocaleDateString('es-AR');
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-24px)] w-full flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-slate-400 mb-4" size={32} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-300 text-[10px]">Cargando Presupuestos...</p>
      </div>
    );
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-[calc(100vh-24px)] font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER (Estilo Productos) */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <FileText size={20} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-900 uppercase tracking-tight leading-none">Presupuestos</h1>
            <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
              Ofertas y reservas pendientes
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-stretch gap-2">
            <button 
                onClick={() => navigate('/sistema/presupuestos/config')}
                className="bg-white border border-slate-200 text-slate-500 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-slate-50 transition-all shadow-sm"
            >
                Configuración
            </button>
            <button 
                onClick={() => { setLoading(true); fetchPresupuestos().then(() => setLoading(false)); }} 
                className="bg-[#0B2A36] text-white px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[#0f3645] transition-all shadow-md"
            >
                <RefreshCcw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
            </button>
        </div>
      </div>

      {/* KPI Cards (Estilo Productos) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
            <div className="p-3 bg-amber-50 text-amber-500 rounded-xl">
                <Clock size={20} />
            </div>
            <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pendientes (Vigentes)</p>
                <h3 className="text-2xl font-black text-slate-800 tracking-tighter mt-1">{sortedDataFiltrada.length}</h3>
            </div>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
            <div className="p-3 bg-emerald-50 text-emerald-500 rounded-xl">
                <DollarSign size={20} />
            </div>
            <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Monto Potencial</p>
                <h3 className="text-2xl font-black text-emerald-600 tracking-tighter mt-1">$ {totalPendiente.toLocaleString('es-AR')}</h3>
            </div>
        </div>
      </div>

      {/* FILTROS (Estilo Productos) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3.5 sm:p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-3">
            <div className="sm:col-span-2 xl:col-span-8">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                  <Search size={12} /> Buscar Presupuesto <FilterHint text="Filtra por cliente o identificador interno del presupuesto." />
                </label>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    <input 
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] focus:bg-white transition-all shadow-inner"
                        placeholder="BUSCAR POR CLIENTE O ID..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                    />
                </div>
            </div>
            <div className="xl:col-span-4 relative">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
                  <Calendar size={12} /> Filtrar Fecha <FilterHint text="Si elegís una fecha, se muestran solo los presupuestos creados ese día." />
                </label>
                <div className="flex items-center gap-2">
                    <input 
                        type="date" 
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all text-slate-600 shadow-inner"
                        value={filtroFecha}
                        onChange={e => { setFiltroFecha(e.target.value); fetchPresupuestos(); }}
                        title="Filtrar por fecha específica (Opcional)"
                    />
                    {filtroFecha && (
                        <button onClick={() => { setFiltroFecha(''); fetchPresupuestos(); }} className="p-2.5 text-rose-500 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-xl transition-all">
                            <X size={16}/>
                        </button>
                    )}
                </div>
            </div>
        </div>
      </div>

      {/* TABLA (Estilo Productos / Dark Header) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
        <div className="bg-[#0B2A36] px-4 py-3.5 sm:px-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Listado de Presupuestos ({sortedDataFiltrada.length})
            </span>
          </div>
        </div>

        <div className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
            {sortedDataFiltrada.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center opacity-60">
                    <FileText size={40} strokeWidth={1} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        {filtroFecha ? 'No hay presupuestos en esta fecha' : 'No hay presupuestos pendientes'}
                    </p>
                </div>
            ) : (
                sortedDataFiltrada.map(item => {
                    const isExpanded = expandedId === item.id;
                    const isProcessing = processingId === item.id;

                    return (
                        <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                            <button
                                type="button"
                                onClick={() => setExpandedId(isExpanded ? null : item.id)}
                                className="w-full text-left"
                                title="Ver detalle del presupuesto"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-[11px] font-black uppercase text-slate-800 truncate">{item.clients?.full_name || 'Consumidor Final'}</div>
                                        <div className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                                            {new Date(item.created_at).toLocaleDateString('es-AR')} · Vence {getVencimiento(item.created_at)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <div className="text-right">
                                            <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Total</div>
                                            <div className="mt-1 text-[12px] font-black text-emerald-600">$ {Number(item.total).toLocaleString('es-AR')}</div>
                                        </div>
                                        <span className={`p-2 rounded-xl transition-all border ${isExpanded ? 'bg-slate-100 text-slate-600 border-slate-200' : 'text-slate-400 border-transparent bg-slate-50'}`}>
                                            {isExpanded ? <X size={16} /> : <ArrowRight size={16} />}
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Sucursal</div>
                                        <div className="mt-1 font-bold uppercase text-slate-700">{item.sitios?.nombre || 'General'}</div>
                                    </div>
                                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                                        <div className="text-[8px] font-black uppercase tracking-widest text-slate-400">Referencia</div>
                                        <div className="mt-1 font-bold text-slate-700">#{String(item.id).slice(0, 8).toUpperCase()}</div>
                                    </div>
                                </div>
                            </button>

                            {isExpanded && (
                                <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-3 animate-in fade-in slide-in-from-top-2">
                                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
                                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                            <ShoppingBag size={14} /> Detalle del Presupuesto
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); imprimirPresupuesto(item); }}
                                            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[9px] font-bold text-slate-600 hover:text-[#51A7FD] hover:border-[#51A7FD] transition-all"
                                        >
                                            <Printer size={12} /> Imprimir PDF
                                        </button>
                                    </div>

                                    <div className="mt-3 space-y-2">
                                        {item.sale_items?.map((prod, idx) => (
                                            <div key={idx} className="flex items-start justify-between gap-3 rounded-xl bg-white px-3 py-2 border border-slate-100 text-[10px]">
                                                <div className="min-w-0">
                                                    <div className="font-black text-slate-700 uppercase truncate">{prod.products?.name || 'Item'}</div>
                                                    <div className="mt-1 text-[9px] font-bold text-slate-400">{prod.quantity} x $ {Number(prod.unit_price).toLocaleString('es-AR')}</div>
                                                </div>
                                                <span className="font-mono font-bold text-slate-600 shrink-0">$ {Number(prod.unit_price * prod.quantity).toLocaleString('es-AR')}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 p-3">
                                        <p className="text-[10px] text-amber-700 leading-relaxed flex gap-2">
                                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                            Al confirmar, se descontará el stock inmediatamente y se registrará el ingreso en caja.
                                        </p>
                                    </div>

                                    <div className="mt-3 flex flex-col gap-2">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleConfirmar(item); }}
                                            disabled={isProcessing}
                                            className="w-full py-3 bg-[#3ff2aa] text-[#0B2A36] rounded-xl font-black uppercase tracking-widest text-[10px] shadow-md hover:brightness-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                                        >
                                            {isProcessing ? <RefreshCcw className="animate-spin" size={16}/> : <Check size={16} strokeWidth={3} />}
                                            Confirmar y Vender
                                        </button>

                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleCancelar(item.id); }}
                                            disabled={isProcessing}
                                            className="w-full py-3 bg-white border border-rose-100 text-rose-500 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-rose-50 transition-all flex items-center justify-center gap-2"
                                        >
                                            <Trash2 size={16} /> Cancelar Presupuesto
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>

        <div className="hidden lg:block w-full overflow-x-auto">
            <table className="min-w-[1020px] w-full text-left border-collapse">
                <thead className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    <tr>
                        <SortableTh label="Fecha" sortKey="created_at" title="Ordenar por fecha de creación" />
                        <SortableTh label="Cliente" sortKey="cliente" title="Ordenar por cliente" />
                        <SortableTh label="Sucursal" sortKey="sucursal" title="Ordenar por sucursal" />
                        <SortableTh label="Total" sortKey="total" align="right" title="Ordenar por importe total" />
                        <th className="px-3.5 py-3 text-center">Acciones</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 font-sans">
                    {sortedDataFiltrada.length === 0 ? (
                         <tr>
                            <td colSpan="5" className="py-20 text-center opacity-40">
                                <FileText size={48} strokeWidth={1} className="mx-auto mb-4 text-slate-300" />
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                    {filtroFecha ? 'No hay presupuestos en esta fecha' : 'No hay presupuestos pendientes'}
                                </p>
                            </td>
                        </tr>
                    ) : (
                        sortedDataFiltrada.map(item => {
                            const isExpanded = expandedId === item.id;
                            const isProcessing = processingId === item.id;

                            return (
                                <React.Fragment key={item.id}>
                                    <tr className="hover:bg-slate-50 transition-all cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                                        <td className="px-4 py-3.5">
                                            <div className="text-[11px] font-bold text-slate-700 whitespace-nowrap">
                                                {new Date(item.created_at).toLocaleDateString('es-AR')}
                                            </div>
                                            <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wide mt-0.5">
                                                Vence: {getVencimiento(item.created_at)}
                                            </div>
                                        </td>
                                        <td className="px-3.5 py-3.5">
                                            <div className="flex items-center gap-2">
                                                <User size={14} className="text-slate-300" />
                                                <span className="text-[11px] font-black text-slate-700 uppercase">{item.clients?.full_name || 'Consumidor Final'}</span>
                                            </div>
                                        </td>
                                        <td className="px-3.5 py-3.5 text-[10px] font-bold text-slate-500 uppercase">
                                            {item.sitios?.nombre || 'General'}
                                        </td>
                                        <td className="px-3.5 py-3.5 text-right text-[12px] font-black text-emerald-600">
                                            $ {Number(item.total).toLocaleString('es-AR')}
                                        </td>
                                        <td className="px-3.5 py-3.5 text-center">
                                            <button className={`p-2 rounded-xl transition-all border ${isExpanded ? 'bg-slate-100 text-slate-600 border-slate-200' : 'text-slate-400 border-transparent hover:text-slate-600 hover:bg-slate-50'}`}>
                                                {isExpanded ? <X size={16} /> : <ArrowRight size={16} />}
                                            </button>
                                        </td>
                                    </tr>

                                    {isExpanded && (
                                        <tr className="bg-slate-50/50 animate-in fade-in slide-in-from-top-2">
                                            <td colSpan="5" className="p-3.5 sm:p-4">
                                                <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                                                    <div className="flex flex-wrap justify-between items-center gap-2 mb-3 border-b border-slate-100 pb-3">
                                                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                            <ShoppingBag size={14} /> Detalle del Presupuesto
                                                        </div>
                                                        <button onClick={(e) => {e.stopPropagation(); imprimirPresupuesto(item)}} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl text-[9px] font-bold text-slate-600 hover:text-[#51A7FD] hover:border-[#51A7FD] transition-all">
                                                            <Printer size={12} /> Imprimir PDF
                                                        </button>
                                                    </div>

                                                    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.8fr)] gap-4">
                                                        <div className="space-y-2">
                                                            {item.sale_items?.map((prod, idx) => (
                                                                <div key={idx} className="flex justify-between items-center text-[11px] py-2.5 px-3 rounded-xl bg-slate-50 border border-slate-100">
                                                                    <div className="flex gap-2">
                                                                        <span className="font-bold text-slate-400 w-6 text-right">{prod.quantity}x</span>
                                                                        <span className="font-bold text-slate-700 uppercase">{prod.products?.name || 'Item'}</span>
                                                                    </div>
                                                                    <span className="font-mono text-slate-600 font-bold">$ {Number(prod.unit_price * prod.quantity).toLocaleString('es-AR')}</span>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <div className="flex flex-col justify-center gap-3 xl:border-l xl:border-slate-100 xl:pl-4">
                                                            <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl">
                                                                <p className="text-[10px] text-amber-700 leading-relaxed flex gap-2">
                                                                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                                                    Al confirmar, se descontará el stock inmediatamente y se registrará el ingreso en caja.
                                                                </p>
                                                            </div>

                                                            <button 
                                                                onClick={(e) => {e.stopPropagation(); handleConfirmar(item)}}
                                                                disabled={isProcessing}
                                                                className="w-full py-3 bg-[#3ff2aa] text-[#0B2A36] rounded-xl font-black uppercase tracking-widest text-[10px] shadow-md hover:brightness-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                                                            >
                                                                {isProcessing ? <RefreshCcw className="animate-spin" size={16}/> : <Check size={16} strokeWidth={3} />}
                                                                Confirmar y Vender
                                                            </button>

                                                            <button 
                                                                onClick={(e) => {e.stopPropagation(); handleCancelar(item.id)}}
                                                                disabled={isProcessing}
                                                                className="w-full py-3 bg-white border border-rose-100 text-rose-500 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-rose-50 transition-all flex items-center justify-center gap-2"
                                                            >
                                                                <Trash2 size={16} /> Cancelar Presupuesto
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 20px; }
      `}</style>
    </div>
  );
};

export default Presupuestos;
